import type { MetricCategory } from './metricsConfig';

export type DetailedMetricType = 'location' | 'comparison';

export type MetricGroups = readonly (readonly string[])[];

export interface SubMetricConfig {
  readonly metricKey: string;
  readonly label?: string; // Defaults to the label of the metric itself
}

export interface DetailedMetricConfig {
  readonly type: DetailedMetricType;
  readonly category?: MetricCategory; // Required for location type (when not using metricGroups)
  readonly metricGroups?: MetricGroups; // Alternative to category - exact metrics for location type, one entry per displayed row
  readonly metricKey?: string; // Required for comparison type
  readonly subMetric?: SubMetricConfig; // For comparison type
  readonly variant?: 'compact' | 'extensive'; // For location type
  readonly cardWidth?: { readonly sm?: number; readonly md?: number; readonly lg?: number }; // Grid width of a location card, defaults to sm 6 / lg 4
}

export interface ViewConfig {
  readonly title: string;
  readonly showDateSelector?: boolean;
  readonly totalMetricsCategory: MetricCategory;
  readonly totalMetricGroups?: MetricGroups; // Overrides the category order for the totals section, one entry per displayed row
  readonly detailedMetrics: DetailedMetricConfig[];
}

// Order of the finance totals at the top of the view.
const FINANCE_TOTAL_GROUPS: MetricGroups = [
  ['schoolFees', 'schoolFeesActive', 'schoolFeesDifference'],
  ['payingStudents', 'activeStudents', 'lastBankImport'],
  ['lastPaymentReminderDate', 'openPaymentReminderSum', 'paymentRemindersSentLastMonth'],
  ['financialCertsCreated', 'financialCertsSent', 'schoolFeeReductionCount'],
  ['schoolFeeReductionTotal', 'totalDifference', 'averageSchoolFee']
];

// Compared across all locations, each in its own card, with a second metric below the value.
const FINANCE_COMPARISONS: readonly { metricKey: string; subMetric: SubMetricConfig }[] = [
  { metricKey: 'averageSchoolFee', subMetric: { metricKey: 'payingStudents' } },
  { metricKey: 'bankBalance', subMetric: { metricKey: 'lastBankImport', label: 'Letzter Import' } },
  { metricKey: 'schoolFeeReductionTotal', subMetric: { metricKey: 'schoolFeeReductionCount' } },
  { metricKey: 'openPaymentReminderSum', subMetric: { metricKey: 'paymentRemindersSentLastMonth', label: 'versendet (letzte 30 Tage)' } }
];

// The remaining metrics, shown per location in the compact cards.
const FINANCE_METRIC_GROUPS: MetricGroups = [
  ['schoolFees', 'schoolFeesActive', 'schoolFeesDifference'],
  ['payingStudents', 'activeStudents', 'lastPaymentReminderDate'],
  ['financialCertsCreated', 'financialCertsSent', 'totalDifference']
];

export const flattenMetricGroups = (groups: MetricGroups): string[] => groups.flatMap((group) => [...group]);

export const getMetricGroupSizes = (groups: MetricGroups): number[] => groups.map((group) => group.length);

export const VIEW_CONFIGS: Record<string, ViewConfig> = {
  home: {
    title: 'Dashboard',
    showDateSelector: true,
    totalMetricsCategory: 'dashboard',
    detailedMetrics: [
      {
        type: 'location',
        metricGroups: [['activeStudents', 'activeEmployees', 'schoolFees']],
        variant: 'compact'
      }
    ]
  },
  finance: {
    title: 'Finanzen',
    showDateSelector: true,
    totalMetricsCategory: 'finance',
    totalMetricGroups: FINANCE_TOTAL_GROUPS,
    detailedMetrics: [
      {
        type: 'location',
        metricGroups: FINANCE_METRIC_GROUPS,
        variant: 'compact',
        cardWidth: { sm: 12, lg: 6 }
      },
      ...FINANCE_COMPARISONS.map((comparison) => ({ type: 'comparison' as const, ...comparison }))
    ]
  },
  staff: {
    title: 'Personal',
    showDateSelector: true,
    totalMetricsCategory: 'staff',
    detailedMetrics: [
      {
        type: 'location',
        metricGroups: [
          ['averageAge', 'maleStaff', 'femaleStaff'],
          ['staffCount', 'nonPedagogicalHours', 'nonPedagogicalHoursShare']
        ],
        variant: 'compact'
      },
      {
        type: 'comparison',
        metricKey: 'teacherCount'
      },
      {
        type: 'comparison',
        metricKey: 'pedagogicalHours'
      },
      {
        type: 'comparison',
        metricKey: 'pedagogicalHoursShare'
      }
    ]
  }
};

export const getViewConfig = (viewName: string): ViewConfig => {
  return VIEW_CONFIGS[viewName];
};
